import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Button, Modal, Input, Select, Textarea, Spinner, message } from '../../ui';
import { request } from '../../api/client';
import { inr2 } from '../../lib/format';
import { formatIstDate } from '../../lib/time';
import { FileUploadField } from '../../components/FileUploadField';
import { bankAcctOption } from '../billing/PaymentFormModal';
import { errorText } from './SettlementApproveDialog';

const MODES = ['BANK_TRANSFER', 'UPI', 'CHEQUE', 'CASH', 'CARD', 'OTHER'];
const words = (v?: string | null) => String(v ?? '').replace(/_/g, ' ').toLowerCase();

/**
 * RECORD MONEY RECEIVED BACK.
 *
 * A claim whose reimbursement had already been paid was reopened or sent back. Its wallet and collection parts were
 * unwound exactly when that happened, and the payment itself stays on the bank register as paid — so the only thing
 * left is the money the rep owes back. This records it coming in: how, into which company account, the reference and
 * the proof, for the full amount, once. Nothing here touches a wallet.
 */
export function RecordRecoveryDialog({ reportId, onClose, onDone }: { reportId: string; onClose: () => void; onDone?: () => void }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<string | undefined>();
  const [bank, setBank] = useState<string | undefined>();
  const [reference, setReference] = useState('');
  const [proof, setProof] = useState<string | undefined>();
  const [remarks, setRemarks] = useState('');

  const { data: report, isLoading, isError } = useQuery({
    queryKey: ['expense-report', reportId],
    queryFn: () => request<any>({ url: `/expense-reports/${reportId}` }),
    refetchOnMount: 'always',
  });
  const { data: banks } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
  });

  const outstanding: any[] = (report?.recoveries ?? []).filter((r: any) => r.status === 'OUTSTANDING');
  // The endpoint records the OLDEST outstanding recovery first, so that is the one this dialog describes.
  const rec = outstanding[0] ?? null;
  const owed = rec ? Number(rec.amount) : 0;
  const repName = report?.repName ?? 'the rep';

  const receive = useConfirmedMutation(
    { title: `Record ${inr2(owed)} received from ${repName}?`, content: 'The money owed back is recorded as received. Their wallet and collection cash do not change.', okText: 'Yes, record' },
    {
      mutationFn: () => request({
        url: `/expense-reports/${reportId}/recovery`, method: 'PATCH',
        data: {
          paymentMode: mode, ...(mode !== 'CASH' ? { bankAccountId: bank } : {}),
          ...(reference.trim() ? { paymentReference: reference.trim() } : {}),
          paymentProofUrl: proof, ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
        },
      }),
      onSuccess: () => {
        message.success(`${inr2(owed)} received from ${repName} recorded for ${rec?.recoveryNo ?? report?.reportNo}`);
        qc.invalidateQueries({ queryKey: ['expense-report', reportId] });
        qc.invalidateQueries({ queryKey: ['expense-reports'] });
        qc.invalidateQueries({ queryKey: ['approvals'] });
        qc.invalidateQueries({ queryKey: ['badges'] });
        onDone?.();
        onClose();
      },
      onError: (e: any) => message.error(errorText(e)),
    },
  );

  const missing = !mode ? 'Choose how the money came back.'
    : mode !== 'CASH' && !bank ? 'Choose the company account it came into.'
      : !proof ? 'Attach the receipt proof.' : null;

  return (
    <Modal
      open
      width={640}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={rec ? `Record money received · ${rec.recoveryNo}` : 'Record money received'}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={receive.isPending} disabled={!owed || !!missing} onClick={() => receive.mutate()}>
            {owed ? `Record ${inr2(owed)} received` : 'Record'}
          </Button>
        </>
      }
    >
      {isLoading || !report ? (
        isError ? <div className="text-sm text-danger-600 dark:text-danger-500">Couldn’t load this claim.</div>
          : <div className="flex items-center justify-center gap-2 py-8 text-muted"><Spinner /> Loading…</div>
      ) : !rec ? (
        <div className="rounded-card border border-line bg-surface-2 px-3 py-2.5 text-sm text-muted">
          Nothing is owed back on {report.reportNo} — it has already been recorded as received.
        </div>
      ) : (
        <div className="space-y-4">
          <section className="rounded-card border border-line bg-surface px-3 py-2.5 text-sm">
            <h4 className="mb-1.5 type-overline text-subtle">Why {repName} owes this back</h4>
            <dl className="space-y-1">
              <div className="flex justify-between gap-3">
                <dt className="text-muted">Reimbursement that was paid</dt>
                <dd className="text-right text-content">
                  {[rec.paidDocNo, rec.paidOn ? `paid ${formatIstDate(rec.paidOn)}` : null, rec.paidMode ? `by ${words(rec.paidMode)}` : null].filter(Boolean).join(' · ')}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted">Claim</dt>
                <dd className="text-right text-content">
                  {report.reportNo} {rec.raisedPath === 'HUB_REVERT' ? 'sent back' : 'reopened'}{rec.raisedByName ? ` by ${rec.raisedByName}` : ''}{rec.raisedAt ? ` on ${formatIstDate(rec.raisedAt)}` : ''}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted">Reason</dt>
                <dd className="text-right text-content">{rec.reason}</dd>
              </div>
              <div className="flex justify-between gap-3 border-t border-line pt-1 tabular-nums">
                <dt className="font-medium text-content">Owed back by {repName}</dt>
                <dd className="font-semibold text-warning-600 dark:text-harvest-300">{inr2(owed)}</dd>
              </div>
            </dl>
            <p className="mt-1.5 type-caption text-muted">
              Received in full, once. The reimbursement stays on the bank register as paid — this records the money coming back.
              {outstanding.length > 1 && ` ${outstanding.length} amounts are owed back on this claim; this records the oldest first.`}
            </p>
          </section>

          <div>
            <label className="mb-1 block type-label text-content">Received via <span className="text-danger-600">*</span></label>
            <Select className="w-full" placeholder="How did the money come back?" value={mode} onChange={setMode}
              options={MODES.map((m) => ({ value: m, label: m.replace(/_/g, ' ') }))} />
          </div>
          {mode && mode !== 'CASH' && (
            <div>
              <label className="mb-1 block type-label text-content">Into account <span className="text-danger-600">*</span></label>
              <Select className="w-full" placeholder="Company bank account" value={bank} onChange={setBank}
                options={(banks ?? []).map(bankAcctOption)}
                notFoundContent="No active company bank account — add or reactivate one in Company & Billing setup" />
            </div>
          )}
          <div>
            <label className="mb-1 block type-label text-content">Reference <span className="text-subtle">(UTR / cheque / receipt no.)</span></label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. UTR 526114839027" />
          </div>
          <div>
            <label className="mb-1 block type-label text-content">Receipt proof <span className="text-danger-600">*</span></label>
            <FileUploadField category="receipt" value={proof} onChange={(url) => setProof(url)}
              buttonLabel={proof ? 'Replace proof' : 'Upload screenshot / deposit slip / signed receipt'} />
          </div>
          <div>
            <label className="mb-1 block type-label text-content">Remarks <span className="text-subtle">(optional)</span></label>
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </div>
          {missing && <p className="type-caption text-subtle">{missing}</p>}
        </div>
      )}
    </Modal>
  );
}
